import { useCallback, useEffect, useState } from 'react';
import { additionalServices, services } from '../../../data/services';
import { formatDuration, formatServicePrice } from '../../../utils/helpers';

const pricingImages = [
  { src: '/assets/pricing/lista-de-precios.png', alt: 'Lista general de precios de Mujer Virtuosa', label: 'Lista de precios' },
  { src: '/assets/pricing/precios-disenos-fijos.png', alt: 'Precios de francesa y degradé de Mujer Virtuosa', label: 'Diseños fijos' },
];

export default function Services({ onSelectService }) {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [touchStartX, setTouchStartX] = useState(null);
  const isOpen = lightboxIndex >= 0;

  const close = useCallback(() => setLightboxIndex(-1), []);
  const prev = useCallback(() => setLightboxIndex((i) => (i <= 0 ? pricingImages.length - 1 : i - 1)), []);
  const next = useCallback(() => setLightboxIndex((i) => (i >= pricingImages.length - 1 ? 0 : i + 1)), []);

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX - touchEndX;
    const threshold = 50; // Umbral mínimo de píxeles para detectar el swipe

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        next();
      } else {
        prev();
      }
    }
    setTouchStartX(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, close, prev, next]);

  return (
    <section className="section services" id="servicios">
      <div className="container">
        <div className="section-heading section-heading--split">
          <div>
            <p className="eyebrow">Servicios</p>
            <h2 className="section-title">Cuidado pensado para cada estilo.</h2>
          </div>
          <p>Cada servicio se realiza con tiempo, dedicación y una atención enfocada en el resultado que buscas.</p>
        </div>

        <div className="services__grid">
          {services.map((service, index) => (
            <article className="service-card" key={service.id}>
              <div className="service-card__number" aria-hidden="true">0{index + 1}</div>
              {service.image ? (
                <img className="service-card__image" src={service.image} alt={`Resultado de ${service.name}`} loading="lazy" />
              ) : null}
              <div className="service-card__body">
                <div className="service-card__meta">
                  <span>{formatDuration(service.durationMinutes)}</span>
                  <span>{formatServicePrice(service)}</span>
                </div>
                <h3>{service.name}</h3>
                <p>{service.description}</p>
                <button className="text-button" type="button" onClick={() => onSelectService(service.id)}>
                  Consultar este servicio <span aria-hidden="true">→</span>
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="pricing-showcase pricing-showcase--compact" aria-labelledby="pricing-showcase-title">
          <div className="pricing-showcase__heading">
            <div>
              <p className="eyebrow">Lista de precios</p>
              <h3 id="pricing-showcase-title">Valores claros para elegir con tranquilidad.</h3>
            </div>
            <p>Toca cada imagen para verla en tamaño completo.</p>
          </div>

          <div className="pricing-showcase__grid">
            {pricingImages.map((img, index) => (
              <button
                key={img.src}
                className="pricing-showcase__item"
                type="button"
                onClick={() => setLightboxIndex(index)}
                aria-label={`Ver ${img.label} en tamaño completo`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  width="938"
                  height="1668"
                  loading="lazy"
                />
                <span aria-hidden="true">Toca para ampliar</span>
              </button>
            ))}
          </div>
        </div>

        <div className="add-ons">
          <p className="eyebrow">Detalles adicionales</p>
          <div className="add-ons__list">
            {additionalServices.map((service) => <span key={service.id}>{service.name}</span>)}
          </div>
          <p className="fine-print">El precio final puede variar dependiendo del diseño y los servicios adicionales seleccionados.</p>
        </div>
      </div>

      {/* Lightbox de precios */}
      <div
        className="pricing-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Vista ampliada de precios"
        style={!isOpen ? { display: 'none' } : undefined}
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {isOpen && (
          <>
            <button className="pricing-lightbox__close" type="button" onClick={close} aria-label="Cerrar">✕</button>
            <figure className="pricing-lightbox__content">
              <img src={pricingImages[lightboxIndex].src} alt={pricingImages[lightboxIndex].alt} />
              <figcaption>
                <span>{pricingImages[lightboxIndex].label}</span>
                <small>{lightboxIndex + 1} / {pricingImages.length}</small>
              </figcaption>
            </figure>
            {pricingImages.length > 1 && (
              <>
                <button className="pricing-lightbox__nav pricing-lightbox__nav--prev" type="button" onClick={prev} aria-label="Anterior">‹</button>
                <button className="pricing-lightbox__nav pricing-lightbox__nav--next" type="button" onClick={next} aria-label="Siguiente">›</button>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
